import React, { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { faFileAlt, faHistory, faSparkles } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faArrowAltCircleUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";

import { openNoteHistoryModal } from "~/lib/prose/history";
import RecapHistory from "~/meetingRecorder/RecapHistory";
import Menu from "~/ui/menu";
import { useModals } from "~/ui/modals";

import type { VersionHistory_note$key } from "~/__generated__/VersionHistory_note.graphql";
import type { VersionHistory_viewer$key } from "~/__generated__/VersionHistory_viewer.graphql";

const viewerFragment = graphql`
	fragment VersionHistory_viewer on Viewer {
		account {
			billingAccount {
				hasAccessToVersioning: hasAccessToFeature(feature: versioning)
			}
		}
	}
`;

const noteFragment = graphql`
	fragment VersionHistory_note on Note {
		id
		hasMeetingRecordings
		meetingRecordings {
			id
			...RecapHistory_meetingRecording
		}
	}
`;

export default function VersionHistory({
	note: noteKey,
	viewer: viewerKey,
	selectedRecordingId,
}: {
	note: VersionHistory_note$key;
	viewer: VersionHistory_viewer$key;
	selectedRecordingId?: string;
}) {
	const modals = useModals();
	const note = useFragment(noteFragment, noteKey);
	const viewer = useFragment(viewerFragment, viewerKey);

	const { hasAccessToVersioning } = viewer.account.billingAccount;

	const selectedRecording = useMemo(
		() => note.meetingRecordings?.find(recording => recording.id === selectedRecordingId),
		[note.meetingRecordings, selectedRecordingId],
	);

	const onNoteVersionHistoryClick = React.useCallback(() => {
		openNoteHistoryModal(note.id, viewer.account.billingAccount.hasAccessToVersioning);
	}, [note.id, viewer.account.billingAccount.hasAccessToVersioning]);

	const onRecapVersionHistoryClick = React.useCallback(() => {
		if (!selectedRecording) return;

		modals.open(({ close }) => <RecapHistory meetingRecording={selectedRecording} close={close} />);
	}, [modals, selectedRecording]);

	if (!note.hasMeetingRecordings) {
		return (
			<Menu.Item icon={faHistory} onSelect={onNoteVersionHistoryClick} data-test-id="note-header-dropdown-history-item">
				Note history
			</Menu.Item>
		);
	}

	return (
		<Menu.Sub mobileHeaderTitle="Version History">
			<Menu.SubTrigger
				icon={!hasAccessToVersioning ? faArrowAltCircleUp : faHistory}
				iconProps={{ color: !hasAccessToVersioning ? "var(--billingColor)" : undefined }}
			>
				Version history&ensp;
			</Menu.SubTrigger>
			<Menu.SubContent data-ignore-focus-lock>
				<Menu.Item
					icon={faFileAlt}
					onSelect={onNoteVersionHistoryClick}
					data-test-id="note-header-dropdown-history-item"
				>
					Note history
				</Menu.Item>
				{selectedRecording && (
					<Menu.Item icon={faSparkles} onSelect={onRecapVersionHistoryClick}>
						Recap history
					</Menu.Item>
				)}
			</Menu.SubContent>
		</Menu.Sub>
	);
}
